import { useRef } from 'react';
import { motion, useScroll, useTransform, useSpring } from 'framer-motion';
import { Link } from 'react-router-dom';
import { MapPin, CheckCircle, Phone, ArrowRight, Droplets } from 'lucide-react';

const COSTA_BRAVA_IMAGE = "https://images.unsplash.com/photo-1762254923872-5bdc4210eb90?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNTl8MHwxfHNlYXJjaHwyfHxsdXh1cnklMjBzd2ltbWluZyUyMHBvb2wlMjBzdW5zZXQlMjBtZWRpdGVycmFuZWFuJTIwdmlsbGElMjBvY2VhbiUyMHZpZXd8ZW58MHx8fHwxNzczMzE4ODI3fDA&ixlib=rb-4.1.0&q=85";

const mainLocations = [
  { name: 'Empuriabrava', description: 'Marina residencial más grande de Europa', featured: true },
  { name: 'Roses', description: 'Playas y gastronomía de altura', featured: true },
  { name: 'Figueres', description: 'Capital del Alt Empordà', featured: true },
  { name: 'Cadaqués', description: 'El pueblo blanco del Mediterráneo', featured: true },
];

const otherLocations = [
  'Santa Margarita', 'Llançà', 'Castelló d\'Empúries', 'L\'Escala',
  'Sant Pere Pescador', 'Peralada', 'Vilajuïga', 'El Port de la Selva',
];

// 3D Location Card
const LocationCard3D = ({ location, index }) => {
  const cardRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: cardRef,
    offset: ['start end', 'end start'],
  });

  const rotateY = useTransform(scrollYProgress, [0, 0.5, 1], [index % 2 === 0 ? -20 : 20, 0, index % 2 === 0 ? 10 : -10]);
  const y = useTransform(scrollYProgress, [0, 0.3, 0.7, 1], [60, 0, 0, -30]);
  const scale = useTransform(scrollYProgress, [0, 0.3, 0.7, 1], [0.9, 1, 1, 0.95]);

  const springConfig = { stiffness: 80, damping: 20 };
  const ySpring = useSpring(y, springConfig);

  return (
    <motion.div
      ref={cardRef}
      style={{
        rotateY,
        y: ySpring,
        scale,
        transformStyle: 'preserve-3d',
      }}
      className="group relative bg-gradient-to-br from-sky-50 to-white p-5 sm:p-6 lg:p-8 rounded-2xl sm:rounded-3xl border border-sky-100 hover:border-sky-300 hover:shadow-xl transition-all"
      data-testid={`main-location-${location.name.toLowerCase()}`}
    >
      <div className="absolute top-3 sm:top-4 right-3 sm:right-4">
        <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full bg-sky-500 animate-pulse" />
      </div>
      
      <motion.div 
        className="w-12 h-12 sm:w-14 sm:h-14 rounded-xl sm:rounded-2xl bg-sky-500 flex items-center justify-center mb-4 sm:mb-6 group-hover:scale-110 transition-transform"
        whileHover={{ rotate: 10 }}
      >
        <MapPin className="w-6 h-6 sm:w-7 sm:h-7 text-white" />
      </motion.div>
      
      <h3 className="font-manrope font-bold text-lg sm:text-xl text-slate-900 mb-1 sm:mb-2">
        {location.name}
      </h3>
      <p className="text-slate-600 text-sm sm:text-base">
        {location.description}
      </p>
    </motion.div>
  );
};

export const ZonePage = () => {
  const containerRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ['start start', 'end end'],
  });

  const heroY = useTransform(scrollYProgress, [0, 0.3], ['0%', '20%']);
  const heroScale = useTransform(scrollYProgress, [0, 0.3], [1, 1.1]);

  return (
    <main ref={containerRef} className="pt-16 sm:pt-20" data-testid="zone-page">
      {/* Hero */}
      <section className="relative h-[50vh] sm:h-[55vh] lg:h-[60vh] min-h-[350px] sm:min-h-[400px] lg:min-h-[500px] overflow-hidden">
        <motion.div 
          className="absolute inset-0"
          style={{ y: heroY }}
        >
          <motion.div style={{ scale: heroScale }} className="absolute inset-0">
            <img 
              src={COSTA_BRAVA_IMAGE}
              alt="Costa Brava"
              className="w-full h-[120%] object-cover"
            />
          </motion.div>
          <div className="absolute inset-0 bg-gradient-to-b from-slate-900/60 via-slate-900/40 to-slate-900/80" />
        </motion.div>

        {/* Floating bubbles */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          {[...Array(8)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute rounded-full bg-white/10"
              style={{
                width: 15 + Math.random() * 25,
                height: 15 + Math.random() * 25,
                left: `${Math.random() * 100}%`,
                bottom: -20,
              }}
              animate={{ y: [0, -500], opacity: [0, 0.6, 0] }}
              transition={{ duration: 8 + Math.random() * 5, repeat: Infinity, delay: i * 0.8 }}
            />
          ))}
        </div>

        <div className="relative z-10 h-full flex items-center">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <motion.span
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="inline-flex items-center gap-2 bg-sky-500/20 text-sky-300 px-4 py-2 rounded-full text-xs sm:text-sm font-semibold mb-4 sm:mb-6 backdrop-blur-sm border border-sky-400/20"
            >
              <MapPin className="w-3 h-3 sm:w-4 sm:h-4" />
              Zona de Cobertura
            </motion.span>

            <motion.h1 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="font-manrope font-extrabold text-3xl sm:text-4xl lg:text-5xl xl:text-6xl text-white mb-4 sm:mb-6 tracking-tight px-4"
            >
              Servicio en{' '}
              <span className="text-sky-400">Costa Brava</span>
            </motion.h1>

            <motion.p 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="text-base sm:text-lg text-slate-300 max-w-2xl mx-auto px-4"
            >
              Cubrimos el Alt Empordà y zonas cercanas con servicio profesional.
            </motion.p>
          </div>
        </div>

        {/* Wave */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1200 120" preserveAspectRatio="none" className="w-full h-12 sm:h-16">
            <path d="M0,0 C150,90 350,0 500,50 C650,100 800,10 1000,80 C1100,110 1150,90 1200,90 L1200,120 L0,120 Z" fill="white" />
          </svg>
        </div>
      </section>

      {/* Main Locations */}
      <section className="py-12 sm:py-16 lg:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10 sm:mb-12 lg:mb-16">
            <span className="inline-flex items-center gap-2 bg-sky-100 text-sky-600 px-4 py-2 rounded-full text-xs sm:text-sm font-semibold mb-4 sm:mb-6">
              <Droplets className="w-3 h-3 sm:w-4 sm:h-4" />
              Principales zonas
            </span>
            <h2 className="font-manrope font-extrabold text-2xl sm:text-3xl lg:text-4xl text-slate-900 mb-2 sm:mb-4">
              Presencia en las mejores zonas
            </h2>
            <p className="text-slate-600 text-base sm:text-lg max-w-2xl mx-auto">
              Especializados en las localidades más exclusivas del Alt Empordà
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
            {mainLocations.map((location, index) => (
              <LocationCard3D key={location.name} location={location} index={index} />
            ))}
          </div>
        </div>
      </section>

      {/* Other Locations */}
      <section className="py-12 sm:py-16 lg:py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 sm:mb-10 lg:mb-12">
            <h2 className="font-manrope font-bold text-xl sm:text-2xl text-slate-900 mb-2 sm:mb-4">
              También cubrimos
            </h2>
            <p className="text-slate-600 text-sm sm:text-base">
              Y muchas más localidades
            </p>
          </div>

          <div className="flex flex-wrap justify-center gap-2 sm:gap-3">
            {otherLocations.map((location, index) => (
              <motion.div
                key={location}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.05 }}
                className="flex items-center gap-1.5 sm:gap-2 bg-white px-3 sm:px-4 py-1.5 sm:py-2 rounded-full border border-slate-200 hover:border-sky-300 hover:bg-sky-50 transition-all"
              >
                <CheckCircle className="w-3 h-3 sm:w-4 sm:h-4 text-sky-500" />
                <span className="text-slate-700 font-medium text-sm sm:text-base">{location}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-12 sm:py-16 lg:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-3 gap-4 sm:gap-8 text-center">
            {[
              { number: '+15', label: 'Años' },
              { number: '500+', label: 'Piscinas' },
              { number: '50km', label: 'Radio' },
            ].map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="p-4 sm:p-6 lg:p-8"
              >
                <div className="font-manrope font-extrabold text-3xl sm:text-4xl lg:text-5xl xl:text-6xl text-sky-500 mb-1 sm:mb-2">
                  {stat.number}
                </div>
                <div className="text-slate-600 font-medium text-sm sm:text-base">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-12 sm:py-16 lg:py-20 bg-gradient-to-br from-sky-500 to-blue-600 relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          {[...Array(8)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute rounded-full bg-white/10"
              style={{
                width: 15 + Math.random() * 35,
                height: 15 + Math.random() * 35,
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
              }}
              animate={{ y: [0, -25, 0], scale: [1, 1.2, 1] }}
              transition={{ duration: 4 + Math.random() * 2, repeat: Infinity, delay: i * 0.3 }}
            />
          ))}
        </div>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative">
          <h2 className="font-manrope font-extrabold text-2xl sm:text-3xl lg:text-4xl text-white mb-4 sm:mb-6">
            ¿Estás en nuestra zona?
          </h2>
          <p className="text-base sm:text-lg text-sky-100 mb-6 sm:mb-8">
            Contáctanos y te confirmaremos disponibilidad.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-5">
            <Link
              to="/contacto"
              data-testid="zone-cta"
              className="w-full sm:w-auto group flex items-center justify-center gap-2 bg-white text-sky-600 px-6 sm:px-8 py-3 sm:py-4 rounded-full font-bold text-sm sm:text-base transition-all hover:scale-105 shadow-xl"
            >
              Consultar
              <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5 group-hover:translate-x-1 transition-transform" />
            </Link>
            <a
              href="tel:+34634060878"
              className="flex items-center gap-2 text-white font-medium text-sm sm:text-base hover:text-sky-100"
            >
              <Phone className="w-4 h-4 sm:w-5 sm:h-5" />
              +34 634 06 08 78
            </a>
          </div>
        </div>
      </section>
    </main>
  );
};

export default ZonePage;
